import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { t } from '../lib/i18n';
import { useAuth } from '../lib/auth-context';
import { ApiError } from '../lib/api-client';

export function SignupPage(): JSX.Element {
  const { signup } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const onSubmit = async (ev: FormEvent<HTMLFormElement>): Promise<void> => {
    ev.preventDefault();
    setError(null);
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError(t('errors.invalidEmail'));
      return;
    }
    if (password.length < 8) {
      setError(t('errors.passwordTooShort'));
      return;
    }
    setSubmitting(true);
    try {
      await signup(email, password);
      setSuccess(true);
      // Apos signup, proximo passo e onboarding (assim que confirmar login).
      // F1: navega para /login; confirmacao por email ainda e pre-requisito.
      setTimeout(() => navigate('/login'), 1500);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.code ?? t('common.error'));
      } else {
        setError(t('common.error'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="auth-page" role="main">
      <section className="auth-card">
        <h1>{t('signup.title')}</h1>
        <p>{t('signup.subtitle')}</p>

        {success ? (
          <p role="status" className="success">
            {t('signup.successMessage')}
          </p>
        ) : null}

        <form onSubmit={onSubmit} noValidate>
          <label htmlFor="signup-email">{t('signup.emailLabel')}</label>
          <input
            id="signup-email"
            name="email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />

          <label htmlFor="signup-password">{t('signup.passwordLabel')}</label>
          <input
            id="signup-password"
            name="password"
            type="password"
            autoComplete="new-password"
            required
            minLength={8}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />

          {error !== null ? (
            <p role="alert" className="error">
              {error}
            </p>
          ) : null}

          <button type="submit" disabled={submitting}>
            {submitting ? t('common.loading') : t('signup.submit')}
          </button>
        </form>

        <p>
          <Link to="/login">{t('signup.alreadyHaveAccount')}</Link>
        </p>
      </section>
    </main>
  );
}
